import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Check, X } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 30000;

export const NotificationsBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const intervalRef = useRef(null);

  const fetchItems = async () => {
    try {
      const res = await api.get("/notifications", { params: { limit: 20 } });
      setItems(res.data.notifications || []);
      setUnread(res.data.unread_count || 0);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    fetchItems();
    intervalRef.current = setInterval(fetchItems, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      fetchItems();
    } catch (e) {
      // ignore
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      toast.success("Todas las notificaciones marcadas como leídas");
      fetchItems();
    } catch (e) {
      toast.error("Error al actualizar");
    }
  };

  const handleClick = async (notif) => {
    if (!notif.read) {
      await handleMarkRead(notif.id);
    }
    setOpen(false);
    if (notif.tender_id) {
      navigate(`/licitacion/${notif.tender_id}`);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${id}`);
      fetchItems();
    } catch (err) {
      // ignore
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell"
          className="relative w-9 h-9 flex items-center justify-center hover:bg-secondary transition-colors rounded-none"
          aria-label="Notificaciones"
        >
          <Bell size={20} weight={unread > 0 ? "fill" : "regular"} />
          {unread > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-[#E63946] text-white text-[10px] font-mono font-bold px-1 rounded-none"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0 rounded-none" align="end" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b border-border/40 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-bold text-sm">Notificaciones</h3>
            <p className="text-[11px] font-mono text-muted-foreground">{unread} sin leer</p>
          </div>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllRead}
              className="text-[11px] font-mono rounded-none"
              data-testid="mark-all-read-btn"
            >
              <Check size={12} className="mr-1" /> Marcar todas
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-[400px]">
          {items.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <Bell size={32} className="mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">No hay notificaciones</p>
              <p className="text-[11px] font-mono text-muted-foreground mt-1">
                Configura alertas para recibir avisos
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border/40">
              {items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`w-full px-4 py-3 text-left hover:bg-secondary transition-colors group ${
                    !n.read ? "bg-[#002FA7]/5 border-l-2 border-l-[#002FA7]" : ""
                  }`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-mono text-[#002FA7] uppercase font-bold">
                        {n.alert_name || "Alerta"} · NOG {n.tender_nog}
                      </p>
                      <p className="text-sm font-medium mt-1 line-clamp-2">{n.tender_title}</p>
                      <p className="text-[11px] text-muted-foreground mt-1 truncate">{n.tender_entity}</p>
                      <p className="text-[10px] font-mono text-muted-foreground mt-1">
                        Q{(n.tender_amount || 0).toLocaleString()} · {new Date(n.created_at).toLocaleString("es-GT")}
                      </p>
                    </div>
                    <button
                      onClick={(e) => handleDelete(e, n.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:text-[#E63946]"
                      aria-label="Eliminar"
                      data-testid={`delete-notif-${n.id}`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
        <div className="px-4 py-2 border-t border-border/40">
          <button
            onClick={() => { setOpen(false); navigate("/alertas"); }}
            className="w-full text-[11px] font-mono uppercase text-[#002FA7] hover:underline py-1"
            data-testid="manage-alerts-link"
          >
            Gestionar alertas →
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
